export const DESIGNER_SMART_GUILD_ANNUAL_FEE_DEFAULT = 3500;
export const DESIGNER_SMART_GUILD_MONTHLY_FEE_DEFAULT = 100;

export const DESIGNER_SMART_GUILD_FEATURES = [
  "Regional Homeowner Marketing",
  "Home Design Guild Seal of Approval",
  "Smart-Guild Matching",
  "Design Hub: Reporting",
  "Design Hub: Education",
  "Design Hub: Partner Offers",
] as const;

export const DESIGNER_SMART_GUILD_DUE_NOW_DEFAULT = Number(
  (DESIGNER_SMART_GUILD_ANNUAL_FEE_DEFAULT + DESIGNER_SMART_GUILD_MONTHLY_FEE_DEFAULT).toFixed(2)
);

export const DESIGNER_SMART_GUILD_TOTAL_ANNUAL_FEE_DEFAULT = Number(
  (
    DESIGNER_SMART_GUILD_ANNUAL_FEE_DEFAULT +
    DESIGNER_SMART_GUILD_MONTHLY_FEE_DEFAULT * 12
  ).toFixed(2)
);

export interface DesignerSmartGuildBillingSplit {
  annualFeeAmount: number;
  monthlyFeeAmount: number;
  dueNowAmount: number;
  totalAnnualFeeAmount: number;
}

type PlanLike = {
  name?: unknown;
  slug?: unknown;
  metadata?: unknown;
};

const DESIGNER_SMART_GUILD_FEE_PREFIXES = new Set([
  "annual marketing fee",
  "monthly platform fee",
  "due now",
]);

export const isDesignerSmartGuildPlan = (plan: PlanLike | null | undefined): boolean => {
  if (!plan) {
    return false;
  }

  const metadata =
    plan.metadata && typeof plan.metadata === "object"
      ? (plan.metadata as Record<string, unknown>)
      : {};

  const audience = String(metadata.audience ?? "")
    .trim()
    .toLowerCase();
  if (audience && audience !== "designer") {
    return false;
  }

  const lookup =
    `${String(plan.name ?? "")} ${String(plan.slug ?? "")} ${String(metadata.tier ?? "")} ` +
    `${String(metadata.bundleType ?? "")} ${String(metadata.billingComponent ?? "")}`
      .toLowerCase()
      .replace(/[\s_]+/g, "");

  return lookup.includes("guild") && !lookup.includes("homeowner");
};

export const getDesignerSmartGuildBillingSplit = (
  plan: PlanLike | null | undefined
): DesignerSmartGuildBillingSplit | null => {
  if (!isDesignerSmartGuildPlan(plan)) {
    return null;
  }

  return {
    annualFeeAmount: DESIGNER_SMART_GUILD_ANNUAL_FEE_DEFAULT,
    monthlyFeeAmount: DESIGNER_SMART_GUILD_MONTHLY_FEE_DEFAULT,
    dueNowAmount: DESIGNER_SMART_GUILD_DUE_NOW_DEFAULT,
    totalAnnualFeeAmount: DESIGNER_SMART_GUILD_TOTAL_ANNUAL_FEE_DEFAULT,
  };
};

export const getDesignerSmartGuildDisplayedFeatures = (
  plan: PlanLike | null | undefined,
  features: unknown[] | null | undefined
): string[] => {
  if (isDesignerSmartGuildPlan(plan)) {
    return [...DESIGNER_SMART_GUILD_FEATURES];
  }

  if (!Array.isArray(features)) {
    return [];
  }

  return features
    .map((feature) => String(feature))
    .filter((feature) => {
      const normalized = feature.trim().toLowerCase();
      const featurePrefix = normalized.split(":")[0].trim();
      return !DESIGNER_SMART_GUILD_FEE_PREFIXES.has(featurePrefix);
    });
};
